'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { FormErrorBox } from '../FormErrorBox';
import type { StepChrome } from '../types';
import { Button } from '@/components/Button';

interface StepShellProps {
  heading?: string;
  chrome: StepChrome;
  children: ReactNode;
  onBack: () => void;
  /**
   * Advance or submit. The step validates on click and shows per-field errors
   * rather than the button being disabled — a disabled button tells the
   * applicant they cannot continue but not why.
   */
  onNext: () => void;
  /**
   * Whether the step currently has any validation error. Drives the summary
   * banner above the fields; the per-field messages are the step's own.
   */
  hasError?: boolean;
  isSubmitting?: boolean;
}

/**
 * Shared frame for every step: the summary error banner, an optional heading,
 * the step's fields, the required-fields note and the back/advance buttons
 * whose labels come from the CMS. Step 1 has no back button — `chrome.backLabel`
 * is absent there.
 */
export const StepShell = ({
  heading,
  chrome,
  children,
  onBack,
  onNext,
  hasError = false,
  isSubmitting = false,
}: StepShellProps) => {
  const t = useTranslations('tutorApplication');
  const tCommon = useTranslations('common');
  // Auto-width buttons throughout — the design never stretches them to the
  // full content width.
  //
  // One row style for every step with a back button (2-6): two `lg` buttons
  // centred as a pair. Step 6 used to have a narrower `compact` variant of its
  // own (img_19), capped at `max-w-md` with the buttons pushed to the edges;
  // the CMS labels outgrew the cap and `flex-wrap` stacked them on desktop, so
  // it now shares the one layout. Step 1 has no back button and stays a single
  // centred `md` button (img_17).
  const buttonSize = chrome.backLabel ? 'lg' : 'md';

  return (
    <div>
      {hasError && (
        <div className="mb-8">
          <FormErrorBox role="alert">{t('errorSummary')}</FormErrorBox>
        </div>
      )}

      {heading && <p className="h3 mb-6 text-base font-bold">{heading}</p>}

      <div className="flex flex-col gap-4">{children}</div>

      {/* Shared with the other forms — `common.requiredFields`. */}
      <p className="mt-10 text-sm">{tCommon('requiredFields')}</p>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 gap-x-6">
        {chrome.backLabel && (
          <Button
            variant="secondary"
            size={buttonSize}
            onClick={onBack}
            type="button"
          >
            {chrome.backLabel}
          </Button>
        )}
        <Button
          variant="primary"
          size={buttonSize}
          onClick={onNext}
          type="button"
          disabled={isSubmitting}
        >
          {chrome.ctaLabel}
        </Button>
      </div>
    </div>
  );
};
